import React, {
  useState, useMemo, useEffect, useRef,
} from 'react';
import { useNavigate } from 'react-router';
import MedicationReviewModal from './MedicationReviewModal';
import RosterToolbar from './RosterToolbar';
import { computeTargetGap } from '../logic/oap';
import { buildCarePlanInfoMap } from '../logic/carePlans';
import { toMs } from '../logic/roster';
import { useCarePlans } from '../state/data/carePlan';
import { useLastOutreachDates } from '../state/data/outreach';
import useDemoMode from '../hooks/useDemoMode';
import useNow from '../hooks/useNow';
import BpValue from './BpValue';
import {
  SubtrackCell, NextDueCell, VisitCell, NameCell, AlertStatusCell, GoalsRingCell, OutreachCell, LastReadingCell,
} from './roster/RosterCells';
import { getProgramId } from '@/config/program';

const EMPTY_OUTREACH_MAP = new Map();

/* ── Formatters ───────────────────────────────────────────────── */

function formatIsoDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatIsoShort(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

/* ── Cell renderers ───────────────────────────────────────────── */

// Priority as a status dot; text stays in the designed text tokens (the --wi-status-* fills fail as text).
const PRIO_STYLES = {
  urgent: { dot: 'var(--wi-status-bad)', color: 'var(--wi-text-primary-bad)', label: 'Urgent' },
  // Moderate, not --wi-status-high: orange means an alert and nothing else.
  high:   { dot: 'var(--wi-status-moderate)', color: 'var(--wi-text-primary-moderate)', label: 'High' },
  normal: { dot: 'var(--wi-status-undefined)', color: 'var(--wi-text-secondary)', label: 'Normal' },
  low:    { dot: 'var(--wi-status-good)', color: 'var(--wi-text-primary-good)', label: 'Low' },
};

function truncate(text, max) {
  if (!text) return text;
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

const TASK_LABEL_MAX = 40;

function TaskCell({ title, priority, is_overdue }) {
  if (!title && !priority) return <span className="detail1 text-secondary">—</span>;
  const s = PRIO_STYLES[priority] || PRIO_STYLES.normal;
  const fullLabel = title || s.label;
  const label = truncate(fullLabel, TASK_LABEL_MAX);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, width: '100%' }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0,
      }} />
      <span
        title={is_overdue ? `${fullLabel} — overdue` : fullLabel}
        style={{
          flex: '1 1 auto', minWidth: 0,
          fontSize: 13, fontWeight: 400,
          color: 'var(--wi-text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </div>
  );
}

function OverdueCell({ count }) {
  const n = Number(count) || 0;
  if (n === 0) return <span style={{ fontSize: 13, color: 'var(--wi-text-secondary)' }}>—</span>;
  return (
    <span
      title={`${n} overdue ${n === 1 ? 'task' : 'tasks'}`}
      style={{
        fontSize: 13, fontWeight: 600, color: 'var(--wi-text-primary-bad)',
        background: 'var(--wi-background-bad)', padding: '2px 9px', borderRadius: 10,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {n}
    </span>
  );
}

// 1, not 2 — two chips squeeze each other into "Over…"/"Pre-di…" in the 1.2fr column.
const CONDITIONS_COLLAPSED_MAX = 1;

// TODO(witems): migrate chips to <Tag> if its API can absorb the collapse behavior.
function ConditionPills({ conditions }) {
  const [expanded, setExpanded] = useState(false);
  if (!conditions || conditions.length === 0) return <span style={{ fontSize: 13, color: 'var(--wi-text-secondary)' }}>—</span>;

  const collapsible = conditions.length > CONDITIONS_COLLAPSED_MAX;
  const shown = collapsible && !expanded ? conditions.slice(0, CONDITIONS_COLLAPSED_MAX) : conditions;
  const hiddenCount = conditions.length - shown.length;

  const chips = (
    <>
      {shown.map((c, i) => (
        <span key={i} title={c} style={{
          fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 4,
          background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-secondary)',
          minWidth: 0,
          ...(expanded
            // Expanded: long names wrap inside the cell, not into the next column.
            ? { overflowWrap: 'anywhere' }
            // Collapsed: one line always — the row keeps its default height.
            : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
        }}>
          {c}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span
          aria-label={`${hiddenCount} more condition${hiddenCount === 1 ? '' : 's'}`}
          style={{
            fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
            background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-primary)',
            flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          +{hiddenCount}
        </span>
      )}
    </>
  );

  if (!collapsible) {
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0 }}>
        {chips}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-expanded={expanded}
      title={expanded ? 'Show fewer conditions' : `${conditions.join(' · ')} — click to show all`}
      // The row click navigates to the patient — the toggle must not bubble.
      onClick={(e) => { e.stopPropagation(); setExpanded(x => !x); }}
      style={{
        display: 'flex', gap: 4, alignItems: 'center', minWidth: 0, width: '100%',
        flexWrap: expanded ? 'wrap' : 'nowrap', overflow: 'hidden',
        background: 'none', border: 'none', padding: 0, margin: 0,
        cursor: 'pointer', font: 'inherit', textAlign: 'left',
      }}
    >
      {chips}
    </button>
  );
}

const PRIO_TONE = { urgent: 'bad', high: 'moderate' };

function TasksCell({ count, priority }) {
  if (!count) return <span className="roster-cell__main roster-tone-muted">0</span>;
  return <span className={`roster-cell__main roster-tone-${PRIO_TONE[priority] || 'normal'}`}>{count}</span>;
}

function TargetGapCell({ firstSystolic, lastSystolic }) {
  const gap = computeTargetGap(firstSystolic, lastSystolic);
  const onTarget = gap != null && gap <= 0;
  if (gap == null) return <span style={{ fontSize: 13, color: 'var(--wi-text-secondary)', fontStyle: 'italic' }}>No baseline</span>;
  if (onTarget) return <span className="roster-cell__main roster-tone-good">On target</span>;
  return <span className="roster-cell__main roster-tone-moderate">+{gap}</span>;
}

function DateCell({ value }) {
  if (!value) return <span style={{ fontSize: 13, color: 'var(--wi-text-secondary)' }}>—</span>;
  return <span style={{ fontSize: 13 }}>{value}</span>;
}

function EnrolledCell({ date }) {
  if (!date) return <span className="detail1 text-secondary">—</span>;
  const d = new Date(date);
  if (isNaN(d)) return <span className="detail1 text-secondary">—</span>;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return <EnrolledLine label={formatIsoShort(date)} days={days} />;
}

// Just the enrolment date on one line; the "Nd in program" detail is tooltip-only
// (the column is too narrow to carry both without the accent getting ellipsis-clipped).
function EnrolledLine({ label, days }) {
  const title = days != null ? `${label} · ${days}d in program` : label;
  return (
    <span
      title={title}
      style={{ fontSize: 13, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
      {label}
    </span>
  );
}

/* ── Column definitions per role ──────────────────────────────── */

const COLUMN_DEFS = {
  name:        { label: 'Patient',         flex: '1.2fr' },
  status:      { label: 'Status',          flex: '0.7fr' },
  conditions:  { label: 'Conditions',      flex: '1.2fr' },
  risk:        { label: 'Risk',            flex: '0.6fr' },
  lastReading: { label: 'Last Reading',    flex: '0.8fr' },
  bp:          { label: 'BP',              flex: '0.7fr' },
  tasks:       { label: 'Tasks',           flex: '0.5fr' },
  gap:         { label: 'Target Gap',      flex: '0.7fr' },
  plan:        { label: 'Care Plan',       flex: '0.8fr' },
  outreach:    { label: 'Last Outreach',   flex: '0.8fr' },
  enrolled:    { label: 'Enrolled',        flex: '0.8fr' },
};

const DEFAULT_COLUMNS = ['name', 'status', 'lastReading', 'bp', 'tasks', 'gap'];

/* ── Sort value extractors ────────────────────────────────────── */

const RISK_ORDER = { critical: 0, high: 1, elevated: 2, standard: 3 };

function getSortValue(field, p, carePlanInfoMap, outreachMap, serverMode) {
  // Server-mode rows use nested objects; legacy rows use flat fields
  if (serverMode) {
    switch (field) {
      case 'name':              return `${p.name?.last || ''} ${p.name?.first || ''}`.toLowerCase();
      case 'conditions':        return (p.conditions?.list || []).join(',');
      case 'lastReading':       return toMs(p.lastReading?.epoch) ?? 0;
      case 'bp':                return p.bp?.systolic ?? -1;
      case 'lastHumanOutreach': return toMs(p.lastHumanOutreach?.at) ?? 0;
      case 'enrolled':          return p.enrolled?.date ? new Date(p.enrolled.date).getTime() : 0;
      default:                  return 0;
    }
  }
  switch (field) {
    case 'name':        return `${p.lastname} ${p.firstname}`.toLowerCase();
    case 'status':      return ({ urgent: 0, high: 1, normal: 2, low: 3 })[p.topPriority] ?? 99;
    case 'conditions':  return (carePlanInfoMap.get(String(p.userid))?.conditions || []).join(',');
    case 'risk':        return RISK_ORDER[carePlanInfoMap.get(String(p.userid))?.risk_level] ?? 99;
    case 'lastReading': return p.lastReadingEpoch || p.lastEpoch || 0;
    case 'bp':          return p.lastSystolic ?? -1;
    case 'tasks':       return p.taskCount ?? 0;
    case 'gap':         return computeTargetGap(p.firstSystolic, p.lastSystolic) ?? 999;
    case 'plan':        return carePlanInfoMap.get(String(p.userid))?.updated_at ? new Date(carePlanInfoMap.get(String(p.userid)).updated_at).getTime() : 0;
    case 'outreach':    return outreachMap.get(String(p.userid)) ? new Date(outreachMap.get(String(p.userid))).getTime() : 0;
    case 'enrolled':    return carePlanInfoMap.get(String(p.userid))?.start_date ? new Date(carePlanInfoMap.get(String(p.userid)).start_date).getTime() : 0;
    default:            return 0;
  }
}

/* ── Main Component ───────────────────────────────────────────── */

export default function PatientListTable({
  patients = [],
  serverColumns = null,
  serverPagination = null,
  tableLoading = false,
  showSearch = false,
  showActions = false,
  initialSortField,
  initialSortDir = 'asc',
  externalLoading = false,
  onRowClick,
  sort: controlledSort = null,
  searchValue: controlledSearch = null,
  onSortChange = null,
  onSearchChange = null,
  onLoadMore = null,
  hasMore = false,
  loadingMore = false,
  onMarkRead = null,
  headerExtras = null,
  // The dashboard renders the toolbar itself, pinned above the card.
  externalToolbar = false,
}) {
  const useServerMode = !!serverColumns;
  const isControlled = !!serverPagination;
  const columns = useServerMode ? serverColumns.map(c => c.key) : DEFAULT_COLUMNS;
  const defaultSort = initialSortField || (columns.includes('status') ? 'status' : 'name');

  const [query, setQuery]                   = useState('');
  const navigate                            = useNavigate();
  const [reviewPatient, setReviewPatient]   = useState(null);
  const [sortField, setSortField]           = useState(defaultSort);
  const [sortDir, setSortDir]               = useState(initialSortDir);

  const effectiveSortField = isControlled ? (controlledSort?.field || 'status') : sortField;
  const effectiveSortDir = isControlled ? (controlledSort?.direction || 'asc') : sortDir;

  const demoMode = useDemoMode();
  const now = useNow();
  const plansQuery = useCarePlans();
  const outreachQuery = useLastOutreachDates();

  // apiv2 returns care-plan timestamps as `created`/`modified` (not `created_at`/`updated_at`);
  // buildCarePlanInfoMap reads the apiv2 fields so newest-wins + the Enrolled/Plan cells work.
  const carePlanInfoMap = useMemo(() => buildCarePlanInfoMap(plansQuery.data), [plansQuery.data]);

  // Stable empty map fallback — `?? new Map()` allocates a fresh ref per
  // render during loading, blowing the downstream useMemo cache.
  const outreachMap = useMemo(() => outreachQuery.data ?? EMPTY_OUTREACH_MAP, [outreachQuery.data]);
  const internalLoading = !useServerMode && (plansQuery.isLoading || outreachQuery.isLoading);
  const isLoading = externalLoading || internalLoading;

  const filtered = useMemo(() => {
    if (isControlled) return patients; // server already sorted/filtered/paginated

    const q = query.trim().toLowerCase();
    const list = (showSearch && q)
      ? patients.filter(p => {
          if (useServerMode) {
            const name = `${p.name?.first || ''} ${p.name?.last || ''}`.toLowerCase();
            const conditions = (p.conditions?.list || []).join(' ').toLowerCase();
            const risk = (p.risk?.level || '').toLowerCase();
            const status = (p.status?.priority || '').toLowerCase();
            return (
              name.includes(q) ||
              String(p.userid).includes(q) ||
              String(p.ehr_patient_id || '').includes(q) ||
              conditions.includes(q) ||
              risk.includes(q) ||
              status.includes(q)
            );
          }
          const cpInfo = carePlanInfoMap.get(String(p.userid));
          const conditions = (cpInfo?.conditions || []).join(' ').toLowerCase();
          const risk = (cpInfo?.risk_level || '').toLowerCase();
          const status = (p.topPriority || '').toLowerCase();
          return (
            `${p.firstname} ${p.lastname}`.toLowerCase().includes(q) ||
            `${p.lastname} ${p.firstname}`.toLowerCase().includes(q) ||
            String(p.userid).includes(q) ||
            (p.phone_number || '').includes(q) ||
            conditions.includes(q) ||
            risk.includes(q) ||
            status.includes(q)
          );
        })
      : patients;

    return [...list].sort((a, b) => {
      const va = getSortValue(sortField, a, carePlanInfoMap, outreachMap, useServerMode);
      const vb = getSortValue(sortField, b, carePlanInfoMap, outreachMap, useServerMode);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [patients, query, sortField, sortDir, carePlanInfoMap, outreachMap, showSearch, useServerMode, isControlled]);

  function handleSort(field) {
    if (isControlled && onSortChange) {
      const newDir = (effectiveSortField === field && effectiveSortDir === 'asc') ? 'desc' : 'asc';
      onSortChange(field, newDir);
    } else {
      if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
      else { setSortField(field); setSortDir('asc'); }
    }
  }

  const SortIcon = ({ field }) => {
    const sf = isControlled ? effectiveSortField : sortField;
    const sd = isControlled ? effectiveSortDir : sortDir;
    if (sf !== field) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sd === 'asc' ? '↑' : '↓'}</span>;
  };

  // Column definitions: the board's (logic/dashboard.ts boardColumns) or this table's own legacy set
  // `sortKey` (logic/dashboard.ts boardColumns) is what a header click sends; null = not sortable.
  const serverColMap = useServerMode
    ? Object.fromEntries(serverColumns.map(c => [c.key, {
      label: c.label, unit: c.unit, flex: c.flex, hint: c.hint, sortKey: 'sortKey' in c ? c.sortKey : c.key,
    }]))
    : null;
  const sortKeyOf = (col) => (serverColMap?.[col] ? serverColMap[col].sortKey : col);

  const flexOf = (c) => {
    if (serverColMap && serverColMap[c]) return serverColMap[c].flex || '1fr';
    if (c === 'name') return '1.6fr';
    return COLUMN_DEFS[c]?.flex || '1fr';
  };
  // minmax(0, …): the header and each row are separate grids, so no label may widen its own column.
  const gridCols = columns.map(c => `minmax(0, ${flexOf(c)})`).join(' ') + (showActions ? ' auto auto' : '');
  // Ten columns fit at 1440px; narrower, the header and the rows scroll sideways together.
  const minTableWidth = useServerMode
    ? Math.round(columns.reduce((sum, c) => sum + parseFloat(flexOf(c)), 0) * 100 + (columns.length - 1) * 12 + 40)
    : undefined;

  /* ── Render a cell from server-provided row data ─────────────── */
  function renderServerCell(colKey, row) {
    switch (colKey) {
      case 'name':
        return <NameCell first={row.name?.first} last={row.name?.last} unread={row.unread} blur={demoMode} />;
      case 'alert':
        return <AlertStatusCell level={row.alert?.level} reason={row.alert?.reason} hasReading={toMs(row.lastReading?.epoch) !== null} />;
      case 'task':
        return (
          <TaskCell
            title={row.task?.title}
            priority={row.task?.priority}
            is_overdue={row.task?.is_overdue}
          />
        );
      case 'overdue':
        return <OverdueCell count={row.overdue?.count} />;
      case 'conditions':
        return <ConditionPills conditions={row.conditions?.list} />;
      case 'lastReading':
        return <LastReadingCell epoch={row.lastReading?.epoch} now={now} />;
      case 'bp':
        return <BpValue systolic={row.bp?.systolic} diastolic={row.bp?.diastolic} unit={false} />;
      case 'outreach':
        return <OutreachCell humanAt={row.lastHumanOutreach?.at} aiAt={row.lastAiOutreach?.at} now={now} />;
      case 'oap':
        return <GoalsRingCell oap={row.oap} />;
      case 'subtrack':
        return <SubtrackCell code={row.subtrack ?? null} />;
      case 'next_due':
        return <NextDueCell nextDue={row.nextDue ?? null} now={now} />;
      case 'visit':
        return <VisitCell visit={row.visit ?? null} now={now} />;
      case 'enrolled': {
        const d = row.enrolled;
        if (!d?.date) return <span className="detail1 text-secondary">—</span>;
        return <EnrolledLine label={formatIsoShort(d.date)} days={d.days_in_program ?? null} />;
      }
      default:
        return <span className="detail1 text-secondary">—</span>;
    }
  }

  /* ── Render a cell by column key (legacy mode) ─────────────── */
  function renderCell(col, p) {
    const cpInfo = carePlanInfoMap.get(String(p.userid));
    switch (col) {
      case 'name': {
        const fullName = `${p.firstname} ${p.lastname}`;
        return (
          <span
            className={demoMode ? 'demo-blur' : undefined}
            title={fullName}
            style={{
              fontSize: 14, fontWeight: 500, textTransform: 'capitalize', color: 'var(--wi-text-primary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
            }}
          >
            {fullName}
          </span>
        );
      }
      case 'alert':
        return <span className="detail1 text-secondary">—</span>;
      case 'task':
        return <TaskCell priority={p.topPriority} />;
      case 'overdue':
        return <OverdueCell count={p.overdueCount} />;
      case 'conditions':
        return <ConditionPills conditions={cpInfo?.conditions} />;
      case 'lastReading':
        return <LastReadingCell epoch={p.lastReadingEpoch || p.lastEpoch} now={now} />;
      case 'bp':
        return <BpValue systolic={p.lastSystolic} diastolic={p.lastDiastolic} />;
      case 'tasks':
        return <TasksCell count={p.taskCount} priority={p.topPriority} />;
      case 'gap':
        return <TargetGapCell firstSystolic={p.firstSystolic} lastSystolic={p.lastSystolic} />;
      case 'plan':
        return <DateCell value={formatIsoDate(cpInfo?.updated_at)} />;
      case 'outreach':
        return <DateCell value={formatIsoDate(outreachMap.get(String(p.userid)))} />;
      case 'enrolled':
        return <EnrolledCell date={cpInfo?.start_date} />;
      default:
        return <span className="detail1 text-secondary">—</span>;
    }
  }

  return (
    <div>

      {(showSearch || headerExtras) && !externalToolbar && (
        <RosterToolbar
          showSearch={showSearch}
          value={isControlled ? (controlledSearch || '') : query}
          onChange={(v) => { if (isControlled && onSearchChange) onSearchChange(v); else setQuery(v); }}
          countLabel={isControlled ? `${patients.length} results` : `${filtered.length} of ${patients.length}`}
          extras={headerExtras}
        />
      )}

      <div className={useServerMode ? 'roster-scroll' : undefined}>
      <div style={{ minWidth: minTableWidth }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'end', padding: '12px 20px', gap: 12, borderBottom: '1px solid var(--wi-divider)' }}>
        {columns.map(col => {
          const label = serverColMap?.[col]?.label || COLUMN_DEFS[col]?.label || col;
          const unit = serverColMap?.[col]?.unit;
          const sortKey = sortKeyOf(col);
          const sortable = sortKey !== null;
          return (
            <span
              key={col}
              title={serverColMap?.[col]?.hint}
              style={{
                cursor: sortable ? 'pointer' : 'default', userSelect: 'none',
                color: sortable && effectiveSortField === sortKey ? 'var(--wi-text-primary)' : 'var(--wi-text-secondary)',
                fontSize: 12,
                fontWeight: 500,
                fontFamily: 'var(--wi-font-family)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                paddingLeft: 8,
              }}
              onClick={sortable ? () => handleSort(sortKey) : undefined}
            >
              {label}{unit && <span className="roster-head__unit"> {unit}</span>}{sortable && <> <SortIcon field={sortKey} /></>}
            </span>
          );
        })}
        {showActions && <span className="eyebrow text-secondary">Profile</span>}
        {showActions && <span className="eyebrow text-secondary">Review</span>}
      </div>

      {isLoading ? (
        <div style={{ padding: '8px 20px' }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: gridCols, gap: 12, alignItems: 'center',
              padding: '14px 0', borderBottom: '1px solid var(--wi-divider)',
            }}>
              {columns.map((col, ci) => (
                <div key={ci}>
                  <div className="skeleton-bar" style={{
                    height: col === 'name' ? 14 : 12,
                    width: col === 'name' ? `${60 + (i * 7) % 30}%` : `${45 + (i * 11 + ci * 13) % 35}%`,
                    borderRadius: 4,
                  }} />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p className="body2 text-secondary" style={{ margin: 0 }}>
            {query ? `No patients matching "${query}"` : 'No patients'}
          </p>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {tableLoading && (
            <div className="roster-loading-veil">
              <div className="loading-spinner" />
            </div>
          )}
          <div className="table-component">
            {filtered.map(p => (
              <div
                key={p.userid}
                className="table-cell-component"
                style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 12, alignItems: 'center', cursor: 'pointer' }}
                onClick={() => {
                  if (p.unread?.has_unread && onMarkRead) onMarkRead(p.userid);
                  onRowClick ? onRowClick(p) : navigate(`/team/${getProgramId()}/patient/${p.userid}`);
                }}
              >
                {columns.map(col => (
                  <div key={col} style={{ paddingLeft: 8, minWidth: 0 }}>{useServerMode ? renderServerCell(col, p) : renderCell(col, p)}</div>
                ))}

                {showActions && (
                  <a
                    href={`https://care.withings.com/team/${getProgramId()}/patient/${p.userid}/measurements`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                  >
                    <button className="btn-primary" style={{ whiteSpace: 'nowrap' }}>Profile</button>
                  </a>
                )}
                {showActions && (
                  <button
                    className="btn-secondary"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={e => { e.stopPropagation(); setReviewPatient(p); }}
                  >
                    Review
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
      </div>

      {!isLoading && filtered.length > 0 && isControlled && serverPagination && (
        <RosterFooter
          shown={patients.length}
          total={Number(serverPagination.total_count ?? 0)}
          hasMore={hasMore}
          loadingMore={loadingMore}
          loading={tableLoading}
          onLoadMore={onLoadMore}
        />
      )}

      {reviewPatient && (
        <MedicationReviewModal patient={reviewPatient} onClose={() => setReviewPatient(null)} />
      )}
    </div>
  );
}

/* ── Roster footer — append-on-scroll ────────────────────────── */

// The count stays because "how much work is left" is the question a triage board has to
// answer. The button is the keyboard path AND the no-observer fallback.
function RosterFooter({
  shown, total, hasMore, loadingMore, loading, onLoadMore,
}) {
  const sentinelRef = useRef(null);

  // Viewport-rooted, not card-rooted: the roster wrapper is `overflow: hidden`, so the
  // page is the scroller. 400px lead time fetches before the operator hits the bottom.
  useEffect(() => {
    if (!hasMore || loadingMore) return undefined;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some(e => e.isIntersecting)) onLoadMore?.(); },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  if (!total) return null;

  // "All N" only when we actually hold N. A roster that can't advance to `total` (server
  // says no more, or a duplicate page) reports what's on screen instead of overclaiming.
  const label = shown >= total
    ? `All ${total} patient${total === 1 ? '' : 's'}`
    : `Showing ${shown} of ${total}`;

  return (
    <div style={{ borderTop: '1px solid var(--wi-divider)', padding: '10px 20px' }}>
      <div ref={sentinelRef} aria-hidden="true" />
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <span className="detail1 text-secondary" style={{ fontSize: 12 }} role="status">{label}</span>
        {hasMore && (
          <button
            className="btn-secondary"
            style={{ height: 28, fontSize: 12, whiteSpace: 'nowrap' }}
            onClick={onLoadMore}
            disabled={loadingMore || loading}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}
